/**
 * DataCaptureService - Captures anonymized homebuying activity snapshots
 * for market analytics. No PII is stored - credit scores are bucketed,
 * no names/emails/phone numbers captured.
 */

import { supabase } from '@/integrations/supabase/client';

export interface ActivitySnapshot {
  session_id: string;
  snapshot_type: 'mortgage_calculator' | 'buying_power' | 'smartbuy_comparison' | 'clara_conversation';
  
  // Property & Location
  zip_code?: string;
  state?: string;
  city?: string;
  property_type?: string;
  bedrooms?: number;
  bathrooms?: number;
  square_feet?: number;
  
  // Loan Details
  loan_type?: string;
  home_price?: number;
  down_payment_amount?: number;
  down_payment_percentage?: number;
  interest_rate?: number;
  loan_term?: number;
  credit_score_range?: string;
  
  // Monthly Payment
  monthly_payment_total?: number;
  monthly_principal?: number;
  monthly_interest?: number;
  monthly_taxes?: number;
  monthly_insurance?: number;
  monthly_pmi?: number;
  monthly_hoa?: number;
  
  // Closing Costs
  total_closing_costs?: number;
  
  // Buying Power
  annual_income?: number;
  total_monthly_debts?: number;
  dti_ratio?: number;
  max_affordable_price?: number;
  
  // Borrower Profile
  is_first_time_buyer?: boolean;
  is_veteran?: boolean;
  has_co_borrower?: boolean;
  household_size?: number;
  
  // SmartBuy
  scenarios_compared?: number;
  
  // Clara
  readiness_score?: number;
  topics_explored?: string[];
  
  // Source
  source_page?: string;
  is_widget?: boolean;
  widget_id?: string;
  organization_id?: string;
}

/** Bucket credit score into anonymized ranges */
export const bucketCreditScore = (score: number): string => {
  if (score < 580) return 'below-580';
  if (score <= 619) return '580-619';
  if (score <= 659) return '620-659';
  if (score <= 699) return '660-699';
  if (score <= 739) return '700-739';
  if (score <= 779) return '740-779';
  return '780+';
};

/** Generate or retrieve a persistent anonymous session ID */
const getSessionId = (): string => {
  const key = 'ef-anon-session-id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
};

/** Enrich snapshot with timing data */
const enrichWithTiming = (snapshot: ActivitySnapshot): Record<string, any> => {
  const now = new Date();
  return {
    ...snapshot,
    search_month: now.getMonth() + 1,
    search_quarter: Math.ceil((now.getMonth() + 1) / 3),
    search_year: now.getFullYear(),
    day_of_week: now.getDay(),
    hour_of_day: now.getHours(),
  };
};

class DataCaptureService {
  private queue: ActivitySnapshot[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly BATCH_SIZE = 10;
  private readonly FLUSH_INTERVAL = 5000; // 5 seconds

  /**
   * Capture a mortgage calculator interaction
   */
  captureMortgageCalculator(data: {
    homePrice: number;
    downPayment: number;
    downPaymentPercentage: number;
    interestRate: number;
    loanTerm: number;
    loanType: string;
    creditScore: number;
    propertyType: string;
    zipCode?: string;
    state?: string;
    city?: string;
    isVeteran?: boolean;
    isFirstTimeHomebuyer?: boolean;
    monthlyPayment?: {
      principal: number;
      interest: number;
      taxes: number;
      insurance: number;
      pmi: number;
      hoa: number;
      total: number;
    };
    totalClosingCosts?: number;
    sourcePage?: string;
    isWidget?: boolean;
    widgetId?: string;
    organizationId?: string;
  }) {
    const snapshot: ActivitySnapshot = {
      session_id: getSessionId(),
      snapshot_type: 'mortgage_calculator',
      home_price: data.homePrice,
      down_payment_amount: data.downPayment,
      down_payment_percentage: data.downPaymentPercentage,
      interest_rate: data.interestRate,
      loan_term: data.loanTerm,
      loan_type: data.loanType,
      credit_score_range: bucketCreditScore(data.creditScore),
      property_type: data.propertyType,
      zip_code: data.zipCode,
      state: data.state,
      city: data.city,
      is_veteran: data.isVeteran,
      is_first_time_buyer: data.isFirstTimeHomebuyer,
      monthly_payment_total: data.monthlyPayment?.total,
      monthly_principal: data.monthlyPayment?.principal,
      monthly_interest: data.monthlyPayment?.interest,
      monthly_taxes: data.monthlyPayment?.taxes,
      monthly_insurance: data.monthlyPayment?.insurance,
      monthly_pmi: data.monthlyPayment?.pmi,
      monthly_hoa: data.monthlyPayment?.hoa,
      total_closing_costs: data.totalClosingCosts,
      source_page: data.sourcePage || window.location.pathname,
      is_widget: data.isWidget,
      widget_id: data.widgetId,
      organization_id: data.organizationId,
    };

    this.enqueue(snapshot);
  }

  /**
   * Capture a buying power calculation
   */
  captureBuyingPower(data: {
    annualIncome: number;
    totalMonthlyDebts: number;
    dtiRatio: number;
    maxAffordablePrice: number;
    creditScore: number;
    loanType?: string;
    isFirstTimeBuyer?: boolean;
    isVeteran?: boolean;
    hasCoBorrower?: boolean;
    householdSize?: number;
    state?: string;
    propertyType?: string;
  }) {
    const snapshot: ActivitySnapshot = {
      session_id: getSessionId(),
      snapshot_type: 'buying_power',
      annual_income: data.annualIncome,
      total_monthly_debts: data.totalMonthlyDebts,
      dti_ratio: data.dtiRatio,
      max_affordable_price: data.maxAffordablePrice,
      credit_score_range: bucketCreditScore(data.creditScore),
      loan_type: data.loanType,
      is_first_time_buyer: data.isFirstTimeBuyer,
      is_veteran: data.isVeteran,
      has_co_borrower: data.hasCoBorrower,
      household_size: data.householdSize,
      state: data.state,
      property_type: data.propertyType,
      source_page: window.location.pathname,
    };

    this.enqueue(snapshot);
  }

  /**
   * Capture a SmartBuy comparison
   */
  captureSmartBuyComparison(data: {
    scenariosCompared: number;
    homePrice?: number;
    loanType?: string;
    state?: string;
    zipCode?: string;
    creditScore?: number;
  }) {
    const snapshot: ActivitySnapshot = {
      session_id: getSessionId(),
      snapshot_type: 'smartbuy_comparison',
      scenarios_compared: data.scenariosCompared,
      home_price: data.homePrice,
      loan_type: data.loanType,
      state: data.state,
      zip_code: data.zipCode,
      credit_score_range: data.creditScore ? bucketCreditScore(data.creditScore) : undefined,
      source_page: window.location.pathname,
    };

    this.enqueue(snapshot);
  }

  /**
   * Capture Clara conversation completion data
   */
  captureClaraConversation(data: {
    readinessScore?: number;
    topicsExplored?: string[];
    state?: string;
    loanType?: string;
    homePrice?: number;
    creditScore?: number;
    isFirstTimeBuyer?: boolean;
  }) {
    const snapshot: ActivitySnapshot = {
      session_id: getSessionId(),
      snapshot_type: 'clara_conversation',
      readiness_score: data.readinessScore,
      topics_explored: data.topicsExplored,
      state: data.state,
      loan_type: data.loanType,
      home_price: data.homePrice,
      credit_score_range: data.creditScore ? bucketCreditScore(data.creditScore) : undefined,
      is_first_time_buyer: data.isFirstTimeBuyer,
      source_page: window.location.pathname,
    };

    this.enqueue(snapshot);
  }

  private enqueue(snapshot: ActivitySnapshot) {
    this.queue.push(snapshot);

    if (this.queue.length >= this.BATCH_SIZE) {
      this.flush();
    } else if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => this.flush(), this.FLUSH_INTERVAL);
    }
  }

  async flush() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.BATCH_SIZE);
    const enrichedBatch = batch.map(enrichWithTiming) as any[];

    try {
      const { error } = await supabase
        .from('homebuying_activity_snapshots')
        .insert(enrichedBatch);

      if (error) {
        console.error('[DataCapture] Failed to persist snapshots:', error.message);
        // Re-queue failed items (max 1 retry)
        if (batch.length <= this.BATCH_SIZE) {
          this.queue.unshift(...batch);
        }
      }
    } catch (err) {
      console.error('[DataCapture] Unexpected error:', err);
    }
  }
}

export const dataCaptureService = new DataCaptureService();
